import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Form, Modal, Select, Spin, TabsProps } from 'antd';

import SalesOrderLineDatesEditPanel from '@totem/components/salesOrders/edits/SalesOrderLineDatesEditPanel';
import SalesOrderLineDevicesEditPanel from '@totem/components/salesOrders/edits/SalesOrderLineDevicesEditPanel';
import SalesOrderLineHierarchyEditPanel from '@totem/components/salesOrders/edits/SalesOrderLineHierarchyEditPanel';
import SalesOrderLineServicesEditPanel from '@totem/components/salesOrders/edits/SalesOrderLineServicesEditPanel';
import { SalesOrderLine } from '@totem/components/salesOrders/list/types';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { SALES_ORDERS_ENDPOINT } from '@totem/utilities/endpoints';

const FormItem = Form.Item;
const { Option } = Select;

const STATUS_OPTIONS = [
  'Active',
  'Canceled',
  'Expired',
  'Information',
  'Pending',
  'Pending Dates',
  'Provisioning',
  'Project Complete',
];

const styles = {
  form: { width: '100%' },
  formItem: { paddingBottom: '0', marginBottom: '1rem' },
  spinner: { display: 'flex', justifyContent: 'center', padding: '2rem' },
};

export interface Props {
  salesOrderLine: SalesOrderLine;
  onClose: (updated: boolean) => void;
  defaultTab?: string;
}

const SalesOrderLineEditModal = ({
  salesOrderLine,
  onClose,
  defaultTab = 'status',
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [currentLine, setCurrentLine] =
    useState<SalesOrderLine>(salesOrderLine);
  const [status, setStatus] = useState<string>(salesOrderLine.status ?? '');
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const fetchCurrentLine = useCallback(() => {
    setIsFetching(true);

    fetch(`${SALES_ORDERS_ENDPOINT}/${salesOrderLine.id}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: SalesOrderLine) => {
        setCurrentLine(result);

        setStatus(result.status ?? '');
      })
      .then(() => setIsFetching(false));
  }, [salesOrderLine.id, getApiToken]);

  useEffect(() => {
    fetchCurrentLine();
  }, [fetchCurrentLine]);

  const handleSubPanelChange = () => {
    setHasChanges(true);

    fetchCurrentLine();
  };

  const handleStatusSave = () => {
    setIsSaving(true);

    fetch(`${SALES_ORDERS_ENDPOINT}/${salesOrderLine.id}/status`, {
      method: 'PUT',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ status }),
    }).then(() => {
      setIsSaving(false);

      onClose(true);
    });
  };

  const tabItems: TabsProps['items'] = [
    {
      key: 'hierarchy',
      label: 'Location',
      children: isFetching ? (
        <div style={styles.spinner}>
          <Spin />
        </div>
      ) : (
        <SalesOrderLineHierarchyEditPanel
          salesOrderLine={currentLine}
          onSaved={() => onClose(true)}
        />
      ),
    },
    {
      key: 'services',
      label: 'Services',
      children: isFetching ? (
        <div style={styles.spinner}>
          <Spin />
        </div>
      ) : (
        <SalesOrderLineServicesEditPanel
          salesOrderLine={currentLine}
          onChanged={handleSubPanelChange}
        />
      ),
    },
    {
      key: 'devices',
      label: 'Devices',
      children: isFetching ? (
        <div style={styles.spinner}>
          <Spin />
        </div>
      ) : (
        <SalesOrderLineDevicesEditPanel
          salesOrderLine={currentLine}
          onChanged={handleSubPanelChange}
        />
      ),
    },
    {
      key: 'dates',
      label: 'Dates',
      children: isFetching ? (
        <div style={styles.spinner}>
          <Spin />
        </div>
      ) : (
        <SalesOrderLineDatesEditPanel
          salesOrderLine={currentLine}
          onSaved={() => onClose(true)}
        />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      children: (
        <Form layout="vertical" style={styles.form}>
          <FormItem label="Status" style={styles.formItem}>
            <Select value={status} onChange={setStatus}>
              {STATUS_OPTIONS.map((opt) => (
                <Option key={opt} value={opt}>
                  {opt}
                </Option>
              ))}
            </Select>
          </FormItem>
        </Form>
      ),
    },
  ];

  return (
    <Modal
      open
      title={
        <ModalTitle>
          {`Edit: ${currentLine.nsSalesOrder?.tranId ?? currentLine.id}`}
        </ModalTitle>
      }
      onCancel={() => onClose(hasChanges)}
      footer={
        <ModalFooter>
          <Button onClick={() => onClose(hasChanges)}>Cancel</Button>
          <Button type="primary" onClick={handleStatusSave} loading={isSaving}>
            Save
          </Button>
        </ModalFooter>
      }
      width={700}
    >
      <IBTabs defaultActiveKey={defaultTab} items={tabItems} />
    </Modal>
  );
};

export default SalesOrderLineEditModal;
